import { BACKEND_URL } from '../config/apiBase';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, RefreshCw, 
  Users, FileText, Clock, Activity, Eye, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = BACKEND_URL;

const SecurityDashboard = () => {
  const { user, isAdmin } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [suspiciousActivity, setSuspiciousActivity] = useState([]);
  const [documentTracking, setDocumentTracking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    eventType: '',
    riskLevel: ''
  });

  useEffect(() => {
    // Wait for user to be loaded before checking admin status
    if (user) {
      if (isAdmin()) {
        fetchAllData();
      } else {
        setLoading(false);
      }
    }
  }, [user]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchMetrics(),
        fetchAuditLogs(),
        fetchSuspiciousActivity(),
        fetchDocumentTracking()
      ]);
    } catch (error) {
      console.error('Error fetching security data:', error);
      setError(error.message || 'Error al cargar datos de seguridad');
      toast.error('Error al cargar datos de seguridad');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    const response = await axios.get(`${API_URL}/api/security/metrics`);
    setMetrics(response.data);
  };

  const fetchAuditLogs = async () => {
    const params = new URLSearchParams();
    if (filters.eventType) params.append('event_type', filters.eventType);
    if (filters.riskLevel) params.append('risk_level', filters.riskLevel);
    params.append('limit', '50');
    
    const response = await axios.get(`${API_URL}/api/security/audit-logs?${params}`);
    setAuditLogs(response.data.logs || []);
  };

  const fetchSuspiciousActivity = async () => {
    const response = await axios.get(`${API_URL}/api/security/suspicious-activity`);
    setSuspiciousActivity(response.data.suspicious_ips || []);
  };

  const fetchDocumentTracking = async () => {
    const response = await axios.get(`${API_URL}/api/security/document-tracking?limit=20`);
    setDocumentTracking(response.data.documents || []);
  };

  const getRiskBadge = (level) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[level] || 'bg-gray-100'}>{level.toUpperCase()}</Badge>;
  };

  const getEventIcon = (eventType) => {
    if (eventType.includes('LOGIN_SUCCESS') || eventType.includes('OTP_VERIFIED')) {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
    if (eventType.includes('FAILED') || eventType.includes('DENIED')) {
      return <XCircle className="w-4 h-4 text-red-600" />;
    }
    if (eventType.includes('DOCUMENT')) {
      return <FileText className="w-4 h-4 text-blue-600" />;
    }
    return <Activity className="w-4 h-4 text-gray-600" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading while user is being verified
  if (!user) {
    return (
      <div className="p-8 text-center">
        <RefreshCw className="w-16 h-16 mx-auto text-blue-500 mb-4 animate-spin" />
        <h1 className="text-xl font-bold">Verificando acceso...</h1>
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-red-600">Acceso Denegado</h1>
        <p className="text-muted-foreground mt-2">
          Solo administradores pueden acceder al dashboard de seguridad.
        </p>
        <p className="text-sm text-muted-foreground mt-4">
          Usuario actual: {user?.email} (Rol: {user?.role || 'sin rol'})
        </p>
      </div>
    );
  }

  // Show error state
  if (error && !metrics) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-16 h-16 mx-auto text-amber-500 mb-4" />
        <h1 className="text-2xl font-bold text-amber-600">Error de Carga</h1>
        <p className="text-muted-foreground mt-2">{error}</p>
        <Button className="mt-4" onClick={fetchAllData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="security-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            Dashboard de Seguridad
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitoreo en tiempo real de eventos de seguridad y auditoría
          </p>
        </div>
        <Button onClick={fetchAllData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{metrics.total_logins_24h}</p>
                  <p className="text-xs text-muted-foreground">Logins (24h)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-2xl font-bold">{metrics.failed_logins_24h}</p>
                  <p className="text-xs text-muted-foreground">Fallidos (24h)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{metrics.otp_requests_24h}</p>
                  <p className="text-xs text-muted-foreground">OTPs (24h)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold">{metrics.documents_uploaded_24h}</p>
                  <p className="text-xs text-muted-foreground">Docs Subidos</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Unlock className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{metrics.documents_deleted_24h}</p>
                  <p className="text-xs text-muted-foreground">Docs Eliminados</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={metrics.high_risk_events_24h > 0 ? 'border-red-500' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-5 h-5 ${metrics.high_risk_events_24h > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                <div>
                  <p className="text-2xl font-bold">{metrics.high_risk_events_24h}</p>
                  <p className="text-xs text-muted-foreground">Alto Riesgo</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Suspicious Activity Alert */}
      {suspiciousActivity.length > 0 && (
        <Card className="border-red-500 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Actividad Sospechosa Detectada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>IP</TableHead>
                  <TableHead>Intentos</TableHead>
                  <TableHead>Emails Afectados</TableHead>
                  <TableHead>Último Intento</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suspiciousActivity.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono">{item._id}</TableCell>
                    <TableCell>
                      <Badge variant="destructive">{item.count}</Badge>
                    </TableCell>
                    <TableCell>{item.emails?.join(', ')}</TableCell>
                    <TableCell>{formatDate(item.last_attempt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Audit Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Registro de Auditoría
            </CardTitle>
            <CardDescription>Últimos 50 eventos de seguridad</CardDescription>
            
            {/* Filters */}
            <div className="flex gap-2 mt-2">
              <Select value={filters.riskLevel || "all"} onValueChange={(v) => setFilters(f => ({ ...f, riskLevel: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Riesgo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="low">Bajo</SelectItem>
                  <SelectItem value="medium">Medio</SelectItem>
                  <SelectItem value="high">Alto</SelectItem>
                  <SelectItem value="critical">Crítico</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchAuditLogs}>
                Filtrar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Evento</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Riesgo</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{getEventIcon(log.event_type)}</TableCell>
                      <TableCell className="font-mono text-xs">{log.event_type}</TableCell>
                      <TableCell className="text-xs">{log.user_email_masked || 'N/A'}</TableCell>
                      <TableCell>{getRiskBadge(log.risk_level)}</TableCell>
                      <TableCell className="text-xs">{formatDate(log.timestamp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Document Tracking */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Tracking de Documentos
            </CardTitle>
            <CardDescription>Zero Retention: todos los documentos se eliminan automáticamente</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Archivo</TableHead>
                    <TableHead>Módulo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Creado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documentTracking.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                        No hay documentos en el sistema
                        <br />
                        <span className="text-xs">Zero Retention Policy activa</span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    documentTracking.map((doc, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs max-w-32 truncate">
                          {doc.original_filename}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{doc.module}</Badge>
                        </TableCell>
                        <TableCell>
                          {doc.deleted ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Eliminado
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <Clock className="w-3 h-3 mr-1" />
                              Temporal
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{formatDate(doc.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Security Policies Banner */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Shield className="w-10 h-10 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">Políticas de Seguridad Activas</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Zero Trust</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>OTP Authentication</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Zero Retention</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Full Audit Trail</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityDashboard;
